import { createEventsAdapter, eventsQuery, EVENTS_TIME_LIST_CONFIG } from './eventsAdapter';
import type { EventsTimeList } from './eventsAdapter';
import type { LocalDate } from '@js-joda/core';
import { effect } from '@preact/signals-core';
import type { ReadonlySignal } from '@preact/signals-core';
import type { QueryClient } from '@tanstack/query-core';
import { SymbolTrackedTimeList } from '@thinkalpha/ui-symbol-tracked-time-list';
import type { EventType } from 'src/components/ui/EventList/viewModel';
import { createTimeListTransport, getEventsPageQuery } from 'src/queries/timeList';

export function createEventsList(client: QueryClient): EventsTimeList {
    return new SymbolTrackedTimeList({
        source: {
            sourceId: 'events',
            selectionKind: 'multi-symbol',
            mode: 'live',
            query: eventsQuery(null, []),
            retention: 0,
        },
        adapter: createEventsAdapter(),
        config: EVENTS_TIME_LIST_CONFIG,
        scheduler: {
            now: () => Date.now(),
            schedule(delay, callback) {
                const timer = setTimeout(callback, delay);
                return () => clearTimeout(timer);
            },
        },
        transport: createTimeListTransport(client, getEventsPageQuery),
    });
}

export class SingleSymbolEventsModel implements Disposable {
    readonly list: EventsTimeList;
    readonly #disposables = new DisposableStack();
    constructor(
        client: QueryClient,
        symbol: ReadonlySignal<string>,
        anchor: ReadonlySignal<LocalDate | null>,
        types: ReadonlySignal<readonly EventType[]>,
    ) {
        this.list = this.#disposables.use(createEventsList(client));
        let sourceKey: string | undefined;
        this.#disposables.defer(
            effect(() => {
                const value = symbol.value;
                const query = eventsQuery(anchor.value, types.value);
                const key = JSON.stringify([value, query]);
                if (key === sourceKey) return;
                sourceKey = key;
                this.list.activateSource(
                    {
                        sourceId: key,
                        selectionKind: 'single-symbol',
                        mode: query.anchor ? 'historical' : 'live',
                        query,
                        retention: 0,
                    },
                    value ? [value] : [],
                );
            }),
        );
    }
    [Symbol.dispose]() {
        this.#disposables.dispose();
    }
}
